import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Grid } from "swiper/modules";
import { Box, Checkbox, Typography } from "@mui/material";
import FavoriteIcon from '@mui/icons-material/Favorite';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/grid";
import { SwiperOptions } from "swiper/types";
import clsx from "clsx";


type BookData = {
    id: string | number;
    img_url: string;
    book_name: string;
    book_sub_name: string;
    onClick?: (id: number) => void;
    otherContent?: React.ReactNode;
    checkedLike?: boolean;
    setCheckedLike?: (status: boolean) => void;
    checkedBookmark?: boolean;
    setCheckedBookmark?: (status: boolean) => void;
    audio_popover?: React.ReactNode;
};

type Props = {
    title: string;
    subtitle?: string;
    data: BookData[];
    onViewAll?: () => void;
    maxWidth?: React.CSSProperties['maxWidth']; // default 1920
    hidden_header?: boolean;
    viewAllButton?: React.ReactNode;
    is_recommend?: boolean;
    hidden_like_bookmark?: boolean;
    disabled_check?: boolean;
    breakpoints?: SwiperOptions['breakpoints'];
    allow_shadow?: boolean;

};
export const ImgBoxList = ({ img_url, book_name, disabled, is_recommend, audio_popover, hidden_like_bookmark, checkedLike, setCheckedLike, checkedBookmark, allow_shadow, setCheckedBookmark }: {
    img_url: string,
    book_name: string,
    is_recommend?: boolean,
    hidden_like_bookmark?: boolean;
    checkedLike?: boolean;
    setCheckedLike?: (status: boolean) => void;
    checkedBookmark?: boolean;
    setCheckedBookmark?: (status: boolean) => void;
    disabled?: boolean;
    allow_shadow?: boolean;
    audio_popover?: React.ReactNode;
}) => {
    return <div
        className={clsx("relative w-full overflow-hidden", {
            'shadow-md': allow_shadow
        })}
        style={{
            aspectRatio: "3 / 4",
            height: is_recommend ? "clamp(120px, 22vw, 162px)" : "clamp(180px, 22vw, 300px)",
            borderRadius: "1rem",
        }}
    >
        <img
            src={img_url}
            alt={book_name}
            className={clsx("absolute hover:scale-105 transition inset-0 w-full h-full object-cover")}
            loading="lazy"
        />
        {audio_popover && (
            <div className="absolute top-2 left-2 flex flex-col gap-2">
                <Box onClick={(e) => {
                    e.stopPropagation();
                }} className="flex flex-col gap-1">
                    {audio_popover}
                </Box>
            </div>
        )}
        {!hidden_like_bookmark && (
            <div className="absolute top-2 right-2 flex flex-col gap-2">
                <Box onClick={(e) => {
                    e.stopPropagation();
                }} className="flex flex-col gap-1">
                    <Checkbox
                        sx={{ p: 0 }}
                        checked={checkedBookmark}
                        disabled={disabled}
                        onChange={(e) => setCheckedBookmark?.(e.target.checked)}
                        checkedIcon={<BookmarkIcon sx={{ color: 'skyblue' }} />}
                        icon={<BookmarkIcon sx={{ color: 'white' }} />}
                    />
                    <Checkbox
                        sx={{ p: 0 }}
                        checked={checkedLike}
                        disabled={disabled}
                        onChange={(e) => setCheckedLike?.(e.target.checked)}
                        checkedIcon={<FavoriteIcon sx={{ color: 'red' }} />}
                        icon={<FavoriteIcon sx={{ color: 'white' }} />}
                    />
                </Box>
            </div>
        )}
    </div>
}
const BookSlide: React.FC<Props> = ({
    title,
    subtitle,
    data,
    onViewAll,
    viewAllButton,
    hidden_header,
    is_recommend,
    hidden_like_bookmark,
    disabled_check,
    breakpoints,
    maxWidth = 1920,
    allow_shadow
}) => {
    return (
        <section
            className="w-full"
            style={{ maxWidth, marginInline: "auto" }}
        >
            {!hidden_header && (
                <div className="flex items-start justify-between gap-3 px-4 sm:px-6 lg:px-0 mb-4 md:mb-6">
                    <div className="min-w-0">
                        <h2 className="font-semibold leading-tight"
                            style={{ fontSize: "clamp(18px, 2vw, 28px)" }}>
                            {title}
                        </h2>
                        {subtitle && (
                            <p className="text-neutral-500 mt-1"
                                style={{ fontSize: "clamp(12px, 1.1vw, 16px)" }}>
                                {subtitle}
                            </p>
                        )}
                    </div>

                    {data.length > 0 && (
                        <>
                            {viewAllButton ? (
                                <>
                                    {viewAllButton}
                                </>
                            ) : onViewAll ? (
                                <button
                                    onClick={onViewAll}
                                    className="shrink-0 rounded-full border border-emerald-500 text-emerald-600 hover:bg-emerald-50 transition px-4 py-2"
                                    style={{ fontSize: "clamp(12px, 1vw, 16px)" }}
                                >
                                    ดูทั้งหมด
                                </button>

                            ) : <></>}
                        </>
                    )}
                </div>
            )}

            {data?.length > 0 ? (
                <div className="px-2 sm:px-4 lg:px-0">
                    <Swiper
                        modules={[Navigation, Grid]}
                        navigation
                        spaceBetween={12}
                        breakpoints={breakpoints ? breakpoints : is_recommend ? {
                            // มือถือเล็ก
                            320: { slidesPerView: 2, spaceBetween: 12 },
                            360: { slidesPerView: 2.2, spaceBetween: 12 },
                            480: { slidesPerView: 2.6, spaceBetween: 14 },
                            // มือถือใหญ่/แท็บเล็ตเล็ก
                            640: { slidesPerView: 3.2, spaceBetween: 16 },
                            768: { slidesPerView: 4, spaceBetween: 16 },
                            // แท็บเล็ตใหญ่/เดสก์ท็อปเล็ก
                            1024: { slidesPerView: 6, spaceBetween: 18 },
                            1280: { slidesPerView: 6, spaceBetween: 18 },
                            1536: { slidesPerView: 6, spaceBetween: 20 },
                            // ดีไซน์ฐาน 1920px+
                            1720: { slidesPerView: 6, spaceBetween: 20 },
                            1920: { slidesPerView: 6, spaceBetween: 22 },

                        } : {
                            // มือถือเล็ก
                            320: { slidesPerView: 2, spaceBetween: 12 },
                            360: { slidesPerView: 2.2, spaceBetween: 12 },
                            480: { slidesPerView: 2.6, spaceBetween: 14 },
                            // มือถือใหญ่/แท็บเล็ตเล็ก
                            640: { slidesPerView: 3.2, spaceBetween: 16 },
                            768: { slidesPerView: 4, spaceBetween: 16 },
                            // แท็บเล็ตใหญ่/เดสก์ท็อปเล็ก
                            1024: { slidesPerView: 5, spaceBetween: 18 },
                            1280: { slidesPerView: 6, spaceBetween: 18 },
                            1536: { slidesPerView: 7, spaceBetween: 20 },
                            // ดีไซน์ฐาน 1920px+
                            1720: { slidesPerView: 8, spaceBetween: 20 },
                            1920: { slidesPerView: 9, spaceBetween: 22 },
                        }}
                    >
                        {data.map((book) => (
                            <SwiperSlide key={book.id}>
                                <article onClick={() => book?.onClick?.(+book.id)} className={clsx("bg-white rounded-2xl cursor-pointer", {
                                    'overflow-hidden ': !allow_shadow,

                                })}>
                                    <ImgBoxList
                                        book_name={book.book_name}
                                        img_url={book.img_url}
                                        hidden_like_bookmark={hidden_like_bookmark}
                                        is_recommend={is_recommend}
                                        checkedBookmark={book.checkedBookmark}
                                        checkedLike={book.checkedLike}
                                        setCheckedBookmark={book.setCheckedBookmark}
                                        setCheckedLike={book.setCheckedLike}
                                        disabled={disabled_check}
                                        allow_shadow={allow_shadow}
                                        audio_popover={book.audio_popover}
                                    />
                                    <div className="px-3 sm:px-4 pt-3 pb-4">
                                        <h3
                                            className="font-semibold leading-snug"
                                            style={{
                                                fontSize: "clamp(14px, 1.3vw, 20px)",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                            }}
                                            title={book.book_name}
                                        >
                                            {book.book_name}
                                        </h3>
                                        <p
                                            className="text-neutral-500 mt-1"
                                            style={{
                                                fontSize: "clamp(12px, 1vw, 16px)",
                                                display: "-webkit-box",
                                                WebkitLineClamp: 1,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                            }}
                                            title={book.book_sub_name}
                                        >
                                            {book.book_sub_name}
                                        </p>
                                        {book.otherContent}
                                    </div>
                                </article>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            ) : (
                <>
                    <Typography textAlign={"center"} my={4} variant="h6">ไม่พบข้อมูล...</Typography>
                </>
            )}

        </section>
    );
};

export default BookSlide;
